import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Loader2, XCircle, Home, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const { clearCart } = useCart();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(response.data);
          clearCart();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          return;
        }

        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Status check error:', error);
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
        } else {
          setTimeout(() => pollStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollStatus();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen pt-24 md:pt-32 bg-[#FAFAFA]" data-testid="order-confirmation-page">
      <div className="container-elegant py-16">
        <div className="max-w-lg mx-auto text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 rounded-full bg-malva/20 flex items-center justify-center mx-auto mb-8">
                <Loader2 className="w-10 h-10 text-malva animate-spin" />
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-4">
                Processing Your Order
              </h1>
              <p className="text-gray-500">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-8">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-4">
                Order Confirmed!
              </h1>
              <p className="text-gray-600 text-lg mb-2">
                Thank you for your order.
              </p>
              <p className="text-gray-500 mb-8">
                We've sent a confirmation email with your order details.
              </p>

              {orderDetails?.order_id && (
                <div className="card-elegant p-6 mb-8 text-left">
                  <div className="flex justify-between border-b border-gray-100 pb-3 mb-3">
                    <span className="text-gray-500">Order Number</span>
                    <span className="text-gray-800 font-mono">{orderDetails.order_id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className="text-emerald-600 font-sans">Confirmed</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button className="btn-secondary w-full sm:w-auto">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
                <Link to="/order">
                  <Button className="btn-primary w-full sm:w-auto">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Order Again
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-8">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-gray-800 mb-4">
                Something Went Wrong
              </h1>
              <p className="text-gray-500 mb-8">
                We couldn't confirm your payment. Please check your email or contact us for assistance.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/cart">
                  <Button className="btn-secondary w-full sm:w-auto">
                    Return to Cart
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-primary w-full sm:w-auto">
                    Contact Support
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
